import { useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { GroupBase, SelectInstance } from 'react-select';
import type { SensorAlertBundle } from '@mark43/cad-api';
import {
    BlankModal,
    Button,
    FormControl,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Text,
    VStack,
} from '@cad-arc';
import { EventAutocomplete, EventOption } from '../forms/inputs/event-autocomplete';
import { getLinkedEventIds, useLinkSensorAlerts } from './use-link-sensor-alerts';

/**
 * Picks a CAD event to attach a sensor event to.
 *
 * Uses the app's own `EventAutocomplete`, whose option value is the numeric `agencyEventId` —
 * exactly what `/sensors/alerts/link` takes. So no id resolution step, and the dispatcher can
 * attach to any event they can search for rather than only one they happen to have open.
 *
 * Cleared events are included: a sensor event can easily be relevant to an event that has already
 * closed, which is much of the point of attaching one after the fact.
 */
export const AttachToEventModal = observer(function AttachToEventModal({
    alert,
    isOpen,
    onClose,
}: {
    alert: SensorAlertBundle;
    isOpen: boolean;
    onClose: () => void;
}): React.ReactElement {
    const linkSensorAlert = useLinkSensorAlerts();
    const [selectedEvent, setSelectedEvent] = useState<EventOption | null>(null);
    // The modal's initial focus, so the dispatcher can type straight away. A react-select
    // instance has its own `focus()`, which is all `initialFocusRef` calls.
    const eventSearchRef = useRef<SelectInstance<unknown, false, GroupBase<unknown>>>(null);

    const alreadyLinkedEventIds = getLinkedEventIds(alert);
    const isAlreadyLinked =
        selectedEvent !== null && alreadyLinkedEventIds.includes(selectedEvent.value);

    function attachAndClose() {
        if (!selectedEvent) {
            return;
        }
        linkSensorAlert({ alert, cadEventId: selectedEvent.value, isLinked: true });
        setSelectedEvent(null);
        onClose();
    }

    return (
        <BlankModal
            isOpen={isOpen}
            initialFocusRef={eventSearchRef}
            onClose={() => {
                setSelectedEvent(null);
                onClose();
            }}
        >
            <ModalHeader>Attach sensor event to a CAD event</ModalHeader>
            <ModalBody>
                <VStack align="stretch" spacing={2}>
                    <FormControl label="CAD event">
                        <EventAutocomplete
                            selectRef={eventSearchRef}
                            // Controlled: it renders `value ?? defaultValue`, so without `value`
                            // a pick reaches `onChange` but the select goes on showing nothing.
                            // (The associate-entity picker omits it on purpose — it appends each
                            // pick to a list and clears.)
                            value={selectedEvent}
                            defaultValue={null}
                            data-testid="attach-sensor-event-search"
                            includeClearedEvents
                            onChange={(option) => setSelectedEvent(option ?? null)}
                        />
                    </FormControl>
                    {isAlreadyLinked ? (
                        <Text variant="caption" color="warning">
                            This sensor event is already attached to that event.
                        </Text>
                    ) : undefined}
                </VStack>
            </ModalBody>
            <ModalFooter>
                <Button
                    variant="ghost"
                    onClick={() => {
                        setSelectedEvent(null);
                        onClose();
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="solid"
                    isDisabled={!selectedEvent || isAlreadyLinked}
                    onClick={attachAndClose}
                >
                    Attach
                </Button>
            </ModalFooter>
        </BlankModal>
    );
});
